import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnDestroy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskStatusFormSettingsItemInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-form-settings-item.dto';
import type { TaskStatusFormSettingsInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-form-settings.dto';
import {
  TaskFormDto,
  type TaskFormInterface,
  type TaskFormOperation
} from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { BrsPrefixService } from '@zaoblako/web/core/services/prefix/brs-prefix.service';
import { TaskEditPermissions } from '@zaoblako/web/core/types/task-edit-permissions.type';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { PaymentOrderTaskComponent } from '@zaoblako/web/modules/payments/dumbs/payment-order-task/payment-order-task.component';
import { TaskEditFormComponent } from '@zaoblako/web/modules/payments/dumbs/task-edit-form/task-edit-form.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { BudgetClassificationCodeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/budget-classification-code-single-select/budget-classification-code-single-select.component';
import { BankDetailsSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/bank-details-single-select/bank-details-single-select.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { ContractSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contract-single-select/contract-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { PayerStatusSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payer-status-single-select/payer-status-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { FileComponent } from '@zaoblako/web/shared/dumbs/file/file.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { UserCardPreviewComponent } from '@zaoblako/web/shared/dumbs/user-card-preview/user-card-preview.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { PaymentTypePipe } from '@zaoblako/web/shared/pipes/payment-type.pipe';
import { SortTaskStatusByOrderPipe } from '@zaoblako/web/shared/pipes/statuses/sort-task-status-by-order.pipe';
import { TaskKindNamePipe } from '@zaoblako/web/shared/pipes/task-kind-name.pipe';
import moment from 'moment-timezone';
import { MenuItem } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { StepsModule } from 'primeng/steps';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';
import { AgentFindInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-find.dto';
import { ParseUniversalInvoiceMethodEnum } from '@zaoblako/shared/modules/task/enum/parse-universal-invoice-method.enum';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BankDetailsAddModalComponent } from '@zaoblako/web/modules/client/components/contragent/card/bank-details-add-modal/bank-details-add-modal.component';

@Component({
  standalone: true,
  selector: 'crm-task-form',
  templateUrl: './task-form.component.html',
  imports: [
    CommonModule,
    TaskEditFormComponent,
    StepsModule,
    CardModule,
    SingleSelectComponent,
    JoinPipe,
    GoBackButtonComponent,
    ContragentAutoCompleteComponent,
    DirectionSingleSelectComponent,
    InputTextComponent,
    PaymentTypePipe,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    FileComponent,
    FileUploadModule,
    EditorComponent,
    UserGroupMultiSelectComponent,
    PaymentOrderTaskComponent,
    DealAutoCompleteComponent,
    UserCardPreviewComponent,
    DividerModule,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    ApprovalSchemeSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    TaskKindNamePipe,
    BudgetClassificationCodeSingleSelectComponent,
    PayerStatusSingleSelectComponent,
    CheckboxModule,
    CurrencySingleSelectComponent,
    BankDetailsSingleSelectComponent,
    SortTaskStatusByOrderPipe,
    ButtonComponent,
    CompanySingleSelectComponent,
    ContractSingleSelectComponent,
    ProgressSpinnerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskFormComponent extends BaseFormComponent<TaskFormInterface> implements OnDestroy {
  @Input() public task = <TaskFormInterface>{};
  @Input() public operation: TaskFormOperation = 'create';
  public dto = TaskFormDto;

  @Input() public organizations: CompanyFindInterface[] = [];
  @Input() public filials: FilialFindInterface[] = [];
  @Input() public units: UnitFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public priorities: TaskPriorityFindInterface[] = [];
  @Input() public nds: NdsFindInterface[] = [];
  @Input() public banks: BankFindInterface[] = [];
  @Input() public users: UserFindInterface[] = [];
  @Input() public statuses: TaskStatusFindInterface[] = [];
  @Input() public kindLoading = false;
  @Input() public schemes: ApprovalSchemeFindInterface[] = [];
  @Input() public currencies: CurrencyFindInterface[] = [];
  @Input() public bankDetails: BankDetailsFindInterface[] = [];
  @Input() public contragentBankDetails: BankDetailsFindInterface[] = [];
  @Input() public defaultAgent: AgentFindInterface;

  @Output() public bankSearch = new EventEmitter<string>();
  @Output() public changeOrganization = new EventEmitter<CompanyFindInterface>();
  @Output() public reloadOrganizationBankDetails = new EventEmitter<void>();
  @Output() public changeResponsible = new EventEmitter<UserFindInterface>();
  @Output() public changeContragent = new EventEmitter<ContragentFindInterface>();

  @Input() public permissions: TaskEditPermissions = {};

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public uploadingFiles = false;
  public isInvoiceParsing = false;

  public types: TaskPaymentTypeEnum[] = Object.values(TaskPaymentTypeEnum);
  public kinds: TaskKindEnum[] = Object.values(TaskKindEnum);
  public contragentBankDetailsModalRef?: DynamicDialogRef;


  public get TaskKindEnum() {
    return TaskKindEnum;
  }

  public get canReadDeal() {
    return this.accessService.finance.dealRights.canReadDeal;
  }

  @Input() public activeStep = 0;
  public steps: MenuItem[] = [
    {
      label: 'Выбор статуса и типа'
    },
    {
      label: 'Заполнение полей'
    }
  ];

  public constructor(
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly fileService: FileService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    public readonly brsPrefixService: BrsPrefixService,
    private readonly dialogService: DialogService,
    private readonly accessService: AccessService
  ) {
    super();
  }

  public selectedStatus() {
    this.activeStep = 1;
    this.cdRef.markForCheck();
  }

  public ngOnDestroy(): void {
    this.contragentBankDetailsModalRef?.close();
  }

  public onContragentBankDetailsAddClick(): void {
    if (!this.task.contragent?.id) {
      return;
    }

    this.contragentBankDetailsModalRef = this.dialogService.open(BankDetailsAddModalComponent, {
      header: 'Новые реквизиты контрагента',
      contentStyle: { overflow: 'auto' },
      styleClass: 'frm-filters-ui-v2 frm-dialog-v2 xl:w-5 lg:w-6 md:w-8 sm:w-11 w-full',
      focusOnShow: false,
      baseZIndex: 10000,
      data: {
        contragentId: this.task.contragent.id,
        bankDetails: this.contragentBankDetails
      }
    });
    this.contragentBankDetailsModalRef.onClose.subscribe((bankDetails: BankDetailsFindInterface[]) => {
      if (bankDetails) {
        this.contragentBankDetails = [...bankDetails];
        this.cdRef.markForCheck();
      }
    });
  }

  public onSave() {
    const saveMethod = () => {
      if (!this.task.orders?.length) {
        this.task.orders = [
          {
            subject: this.task.subject,
            date: this.task.date,
            amount: this.task.amount
          }
        ];
      }

      if (
        this.checkValidate(this.task).valid &&
        !this.checkRequiredFields() &&
        !(this.task.subject?.length > 190 || this.task.orders?.filter((order) => order.subject?.length > 190)?.length)
      ) {
        super.onSave(this.task);
      }
    };

    if (this.task.orders?.length > 1 && !this.isEqualSumTask()) {
      return this.errorService.addCustomError('Ошибка', 'Не совпадают суммы заявки и платежных поручений');
    }

    if (this.task.orders?.length === 1 && !this.isEqualPaymentTask()) {
      this.confirmDialogService.confirm({
        message: 'Не совпадают данные заявки и платежного поручения, обновить данные в платежном поручении?',
        accept: () => {
          this.task.orders?.forEach((order) => {
            order.subject = this.task.subject;
            order.date = this.task.date;
            order.amount = this.task.amount;
          });
          this.cdRef.markForCheck();
          saveMethod();
        },
        reject: () => {
          saveMethod();
        }
      });
    } else {
      saveMethod();
    }
  }

  private checkRequiredFields() {
    this.errors = Object.create({});
    Object.entries(this.task.status.formSettings).forEach(
      ([property, setting]: [keyof TaskStatusFormSettingsInterface, TaskStatusFormSettingsItemInterface]) => {
        if (
          setting.required &&
          ((property !== 'vat' && !this.task[property]) ||
            (property === 'vat' && (typeof this.task[property] === 'undefined' || this.task[property] === null)))
        ) {
          this.errors = {
            ...this.errors,
            [property]: `Поле "${setting.name}" обязательно для заполнения`
          };
        }
      }
    );

    // TODO:
    //   [from Alexander Sychev]: Это пиздец какой костыль. По-хорошему, всю эту форму надо переделывать, вместе с системой валидации.
    if (this.task.kind === TaskKindEnum.PAYMENT_OF_TAXES) {
      if (!this.task.budgetClassificactionCode?.id) {
        this.errors = {
          ...this.errors,
          budgetClassificactionCode: 'Поле "КБК" обязательно для заполнения' as any
        };
      }
      if (!this.task.payerStatus?.id) {
        this.errors = {
          ...this.errors,
          payerStatus: 'Поле "Статус плательщика" обязательно для заполнения' as any
        };
      }
    }

    return Object.keys(this.errors)?.length;
  }

  public uploadFiles(data: FileUploadHandlerEvent) {
    this.uploadingFiles = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.uploadingFiles = false;
        if (this.defaultAgent) {
          this.confirmDialogService.confirm({
            message: 'Выполнить ли автоматическое заполнение реквизитов из счета?',
            accept: () => {
              this.isInvoiceParsing = true;
              this.cdRef.markForCheck();
              this.fileService
                .parseUniversalInvoice(files[0].id, { method: ParseUniversalInvoiceMethodEnum.GPT })
                .subscribe({
                  next: (data) => {
                    for (const key of Object.keys(data)) {
                      if (data[key]) {
                        this.task[key] = data[key];
                      }
                    }

                    if (data.organization) {
                      this.changeOrganization.emit(data.organization);
                    }

                    if (this.task.contragent?.bankDetails?.length) {
                      const hasSelectedBankDetails = this.task.contragent.bankDetails.some(
                        (bankDetail) => bankDetail.isSelected
                      );

                      if (!hasSelectedBankDetails) {
                        this.task.contragent.bankDetails[0].isSelected = true;
                      }
                    }

                    this.task.files = files;
                    this.fileUpload.clear();
                    this.isInvoiceParsing = false;
                    this.cdRef.markForCheck();
                  },
                  error: () => {
                    this.errorService.addCustomError(
                      'Файл загружен, но произошла ошибка при заполнение реквизитов из счета'
                    );
                    this.task.files = files;
                    this.fileUpload.clear();
                    this.isInvoiceParsing = false;
                    this.cdRef.markForCheck();
                  }
                });
            },
            reject: () => {
              this.task.files = files;
              this.fileUpload.clear();
              this.cdRef.markForCheck();
            }
          });
        } else {
          this.task.files = files;
          this.fileUpload.clear();
          this.cdRef.markForCheck();
        }
      },
      error: () => {
        this.uploadingFiles = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(filesArray: FileFindInterface[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        filesArray.splice(filesArray.indexOf(file), 1);
        this.cdRef.markForCheck();
      }
    });
  }

  public changeValue() {
    if (!this.task.nds) {
      return;
    }

    this.task.vat = VatAmountCalculator
      .getInstance()
      .getVatAmount(this.task.amount || 0, this.task.nds?.amount || 0);
  }

  public backStep() {
    this.activeStep = 0;
    this.task.status = null;
    this.errors = {};
    this.cdRef.markForCheck();
  }

  private isEqualPaymentTask(): boolean {
    return (
      this.task.orders?.length &&
      this.task.orders[0].amount === this.task.amount &&
      moment(this.task.orders[0].date).isSame(moment(this.task.date), 'day') &&
      this.task.orders[0].subject.trim().toLowerCase() === this.task.subject.trim().toLowerCase()
    );
  }

  private isEqualSumTask(): boolean {
    return this.task.orders?.reduce((sum, order) => sum + (order.amount || 0), 0) === (this.task.amount || 0);
  }

  public changeScheme(newScheme: ApprovalSchemeFindInterface) {
    const newUserAuditors = newScheme?.userAuditors || [];
    const newUserGroupAuditors = newScheme?.userGroupAuditors || [];
    this.task = {
      ...this.task,
      scheme: newScheme,
      userAuditors: newUserAuditors,
      userGroupAuditors: newUserGroupAuditors
    };
    this.cdRef.markForCheck();
  }

  public changeKind() {
    if (!this.task.kind) {
      this.task.kind = null;
    }

    if (this.task.kind === TaskKindEnum.PAYMENT_OF_SALARY) {
      this.task.type = TaskPaymentTypeEnum.CASH;
    }
  }

  public changeResponsibleUser(user: UserFindInterface) {
    this.changeResponsible.emit(user);
  }

  public changeAmount(amount: string | number) {
    this.task.amount = Number(amount);
  }
}
